import {isMobile} from "react-device-detect";
import {useMemo, useState} from "react";
import {useHistory} from "react-router-dom";
import {DashboardLayout} from "components/Layout";
import {BasicTable, SearchBox} from "components/Table";
import {ButtonAddTable} from "components/Button";
import {ModalConfirmation, ModalInfo} from "components/Modal";
import {useHospitals, useDeleteHospital} from "hooks/SuperAdmin/Hospital";
import {useToast} from "hooks/useToast";
import {useStateCallback} from "hooks/useStateCallback";
import {convertErrorMessageFormat} from "utils/converter";

export function MasterRumahSakitListPage() {
	const [dataLimit, setDataLimit] = useState(10);
	const [searchKeyword, setSearchKeyword] = useState("");
	const {hospitals, isLoading, filterHospitals} = useHospitals(true);

	// Delete Data State
	const {mutate: deleteHospitalById, isLoading: isDeletingData} = useDeleteHospital();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showInfoModal, setShowInfoModal] = useState(false);
	const [deleteItemData, setDeleteItemData] = useStateCallback({id: null, name: null});
	const {showToast} = useToast();

	const history = useHistory();
	const columns = useMemo(
		() => [
			{
				Header: "No",
				accessor: "idSerial",
			},
			{
				Header: "Nama Rumah Sakit",
				accessor: "name",
				maxWidth: 150,
			},
			{
				Header: "Alamat",
				accessor: "address",
				maxWidth: 150,
			},
			{
				Header: "No Telp",
				accessor: "phone",
				maxWidth: 150,
			},
			{
				Header: "Email",
				accessor: "email",
				maxWidth: 150,
			},
			{
				Header: "Aksi",
				accessor: "id",
				Cell: ({row}) => (
					<>
						<a
							class="text-orange wait-pay"
							style={{cursor: "pointer"}}
							onClick={() => history.push(`/master-rumah-sakit/show/${row?.values?.id}`)}>
							<span class="icon-ico-eye-open ft-18" />
						</a>
						<a
							class="text-orange wait-pay"
							style={{cursor: "pointer"}}
							onClick={() => history.push(`/master-rumah-sakit/edit/${row?.values?.id}`)}>
							<span class="icon-ico-pencil ft-18" />
						</a>
						<a
							class="text-orange wait-pay"
							style={{cursor: "pointer"}}
							onClick={() => {
								if (row.original.can_delete) {
									setDeleteItemData(row?.values, () => setShowDeleteModal(true));
								} else {
									setShowInfoModal(true);
								}
							}}>
							<span class="icon-ico-delete ft-18" />
						</a>
					</>
				),
			},
		],
		[],
	);
	const columnsMobile = useMemo(
		() => [
			{
				Header: "No",
				accessor: "idSerial",
				Cell: ({row}) => (
					<>
						<div class="col-4">
							<div class="text-bold">{row.original.name}</div>
						</div>
					</>
				),
			},
			{
				Header: "Nama Rumah Sakit",
				accessor: "name",
				Cell: ({row}) => (
					<>
						<div class="col-5">
							<div class="text-bold">{row.original.phone}</div>
							<div
								class="text-bold"
								style={{textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"}}>
								{row.original.email}
							</div>
						</div>
					</>
				),
			},
			{
				Header: "Alamat",
				accessor: "id",
				Cell: ({row}) => (
					<>
						<div class="col-3">
							<a
								class="text-orange wait-pay"
								style={{cursor: "pointer"}}
								onClick={() => history.push(`/master-rumah-sakit/show/${row?.values?.id}`)}>
								<span class="icon-ico-eye-open ft-18" />
							</a>
							<a
								class="text-orange wait-pay"
								style={{cursor: "pointer"}}
								onClick={() => history.push(`/master-rumah-sakit/edit/${row?.values?.id}`)}>
								<span class="icon-ico-pencil ft-18" />
							</a>
							<a
								class="text-orange wait-pay"
								style={{cursor: "pointer"}}
								onClick={() => {
									if (row.original.can_delete) {
										setDeleteItemData(row?.values, () => setShowDeleteModal(true));
									} else {
										setShowInfoModal(true);
									}
								}}>
								<span class="icon-ico-delete ft-18" />
							</a>
						</div>
					</>
				),
			},
		],
		[],
	);

	const dataWithIDSerial =
		hospitals?.data?.rows?.map((item, index) => ({
			...item,
			idSerial: (hospitals?.data?.currentPage - 1) * dataLimit + index + 1,
		})) || [];

	const handleDeleteData = () => {
		deleteHospitalById(
			{id: deleteItemData.id},
			{
				onSuccess: () => {
					showToast("success", `Data rumah sakit "${deleteItemData.name}" berhasil dihapus`, 3000);
					window.scrollTo(0, 0);
					setShowDeleteModal(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	};

	return (
		<DashboardLayout>
			<>
				<ModalInfo
					approveLabel="OK"
					description="Data tidak dapat dihapus karena sudah digunakan"
					title="Info"
					visible={showInfoModal}
					onApprove={() => setShowInfoModal(false)}
					onClose={() => setShowInfoModal(false)}
				/>
				<ModalConfirmation
					approveLabel="Ya"
					description={`Apakah Anda yakin ingin menghapus data "${deleteItemData.name}"?`}
					loading={isDeletingData}
					rejectLabel="Batal"
					visible={showDeleteModal}
					onApprove={handleDeleteData}
					onClose={() => setShowDeleteModal(false)}
					onReject={() => setShowDeleteModal(false)}
				/>
				<div class="text-bold text-dark mb-4">Data Rumah Sakit</div>
				<SearchBox
					rightElement={
						<ButtonAddTable
							text="Tambah Rumah Sakit"
							onClick={() => history.push("/master-rumah-sakit/tambah")}
						/>
					}
					onSearch={keyword => {
						filterHospitals({search: keyword, page: 1, limit: dataLimit});
						setSearchKeyword(keyword);
					}}
				/>

				<BasicTable
					columns={isMobile ? columnsMobile : columns}
					currentPage={hospitals?.data?.currentPage}
					data={dataWithIDSerial}
					limit={dataLimit}
					loading={isLoading}
					striped={true}
					title="Data Rumah Sakit"
					totalItems={hospitals?.data?.totalItems}
					totalPage={hospitals?.data?.totalPages}
					onLimitChange={limit => {
						filterHospitals({limit, page: 1, search: searchKeyword});
						setDataLimit(limit);
					}}
					onPageChange={selectedPage =>
						filterHospitals({page: selectedPage, limit: dataLimit, search: searchKeyword})
					}
				/>
			</>
		</DashboardLayout>
	);
}
